import { MomentInput } from 'moment'
import { Fragment, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { msToHHMMSS } from 'beam/dist/utils/time'
import Typography from 'beam/dist/Typography'
import { makeStyles } from 'beam/dist/styles'
import { userCanEditComment } from './helpers'
import { useAuthenticatedUser } from '../../../Permissions'
import { Tooltip } from 'beam/dist/Tooltip'
import { useLocalizedMoment } from '../../../moment/useLocalizedMoment'
import { EvalComment } from './evaluationForm.api'
import { IndexedChatBubble } from '../ChatMapPanel/constants'
import { formatDuration } from '../../../Common/Utils'

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
    whiteSpace: 'pre-wrap',
    '&:last-child': {
      border: 'none',
    },
  },
  commentText: {},
  offsetTime: {
    color: theme.palette.text.secondary,
  },
  relativeTime: {
    cursor: 'default',
  },
  editButton: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
    '@media print': {
      display: 'none',
    },
  },
  edited: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.hint,
    cursor: 'default',
  },
  inlinePopper: {
    display: 'inline',
  },
}))

interface CommentProps {
  /** Comment object containing comment text and metadata */
  comment: EvalComment,
  /** Time zone identifier to use when formatting timestamps */
  timezone?: string,
  /** Callback function for when the Edit button is clicked */
  onEdit: (comment: EvalComment) => void,
  /** Chat bubble associated with the comment, if applicable */
  associatedChatBubble: IndexedChatBubble | null,
}

function Comment({ comment, timezone = '', onEdit, associatedChatBubble }: CommentProps): ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()
  const authenticatedUser = useAuthenticatedUser()
  const { formatDateTime, fromNow } = useLocalizedMoment()

  const getRelativeTime = (timestamp: MomentInput) => {
    return fromNow(timestamp, timezone)
  }

  const getFormattedTime = (timestamp: MomentInput) => {
    return formatDateTime(timestamp, 'L LT', timezone)
  }

  const handleEditButtonClick = () => {
    onEdit(comment)
  }

  return (
    <div className={classes.root}>
      <div>
        <Typography variant="body1" display="inline">
          <strong>{`${comment.commentor.displayId}  `}</strong>
        </Typography>
        <Typography variant="caption" display="inline">
          <Tooltip
            title={getFormattedTime(comment.created)}
            placement="top"
            arrow
            PopperProps={{
              disablePortal: true,
            }}
            classes={{ popper: classes.inlinePopper }}
          >
            <span className={classes.relativeTime}>{getRelativeTime(comment.created)}</span>
          </Tooltip>
          {associatedChatBubble ?
            ` • ${t('location')}: ${associatedChatBubble.authorName} - ${formatDuration(associatedChatBubble.relativeTimestampMs)}` :
            comment.contactOffset !== 0 && ` • ${t('location')}: ${msToHHMMSS(comment.contactOffset)}`
          }
          {userCanEditComment(authenticatedUser) && (
            <Fragment>
              {' • '}
              <span
                tabIndex={0}
                role="button"
                className={classes.editButton}
                onClick={handleEditButtonClick}
                onKeyPress={handleEditButtonClick}
              >
                {t('edit')}
              </span>
            </Fragment>
          )}
        </Typography>
        <Typography variant="body1" className={classes.commentText} display="block" component="div">
          {comment.text}
          {comment.history.length > 0 && (
            <Tooltip
              // @ts-expect-error TODO: fix error (with optional chaining?)
              title={`${getFormattedTime(comment.history[0].created)} • ${comment.history[0].commentor.displayId}`}
              placement="bottom"
              arrow
              PopperProps={{
                disablePortal: true,
              }}
              classes={{ popper: classes.inlinePopper }}
            >
              <span className={classes.edited}>({t('editedInfo')})</span>
            </Tooltip>
          )}
        </Typography>
      </div>
    </div>
  )
}

export default Comment
